import { useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import type { EntityMediaSummary } from './entityMediaService'

interface EntityMediaIndicatorProps {
  summary?: EntityMediaSummary
  label: string
  emptyLabel: string
  onOpen: () => void
}

export function EntityMediaIndicator({
  summary,
  label,
  emptyLabel,
  onOpen,
}: EntityMediaIndicatorProps) {
  const count = summary?.count ?? 0
  const buttonRef = useRef<HTMLButtonElement>(null)
  const [previewPosition, setPreviewPosition] = useState<{ top: number; left: number } | null>(null)

  const showPreview = () => {
    const button = buttonRef.current
    if (!button) return

    const rect = button.getBoundingClientRect()
    const width = 224
    const estimatedHeight = 126
    const margin = 8
    const left = Math.min(
      Math.max(rect.left, margin),
      Math.max(window.innerWidth - width - margin, margin),
    )
    const belowTop = rect.bottom + margin
    const top = belowTop + estimatedHeight > window.innerHeight
      ? Math.max(rect.top - estimatedHeight - margin, margin)
      : belowTop

    setPreviewPosition({ top, left })
  }

  if (!count) {
    return <span className="text-slate-400">—</span>
  }

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onMouseEnter={showPreview}
        onMouseLeave={() => setPreviewPosition(null)}
        onFocus={showPreview}
        onBlur={() => setPreviewPosition(null)}
        onClick={(event) => {
          event.stopPropagation()
          onOpen()
        }}
        className="inline-flex items-center gap-1.5 rounded-full bg-sky-50 dark:bg-sky-900/30 px-2 py-1 text-xs font-medium text-sky-700 dark:text-sky-300 hover:bg-sky-100 dark:hover:bg-sky-900/50 transition-colors"
        title={label}
      >
        <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
        <span>{count}</span>
      </button>

      {previewPosition && createPortal(
        <div
          className="pointer-events-none fixed z-[100] w-56 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-2 shadow-xl"
          style={{ top: previewPosition.top, left: previewPosition.left }}
        >
          <span className="mb-2 block text-xs font-semibold text-slate-700 dark:text-slate-200">
            {label} ({count})
          </span>
          {summary?.previews.length ? (
            <span className="grid grid-cols-3 gap-2">
              {summary.previews.map((preview) => (
                <span
                  key={preview.id}
                  className="flex aspect-square items-center justify-center overflow-hidden rounded-md bg-slate-100 dark:bg-slate-900"
                  title={preview.media_name}
                >
                  {preview.media_type === 'image' && preview.signed_url ? (
                    <img
                      src={preview.signed_url}
                      alt={preview.media_name}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <svg className="h-7 w-7 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.7} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  )}
                </span>
              ))}
            </span>
          ) : (
            <span className="text-xs text-slate-500 dark:text-slate-400">{emptyLabel}</span>
          )}
        </div>,
        document.body,
      )}
    </>
  )
}
